import { sanitizeRestrictedLabelText } from '@/lib/utils/displayText'

/**
 * Official CSAT integer scores stored on ticket_requests.csat_score.
 * 1=No Comment, 2=Not Satisfied, 3=Satisfied, 4=Very Satisfied
 * Display order: highest satisfaction first (email + /csat-submit UI).
 */
export const CSAT_RATINGS = [
  { score: 4, label: 'Very Satisfied' },
  { score: 3, label: 'Satisfied' },
  { score: 2, label: 'Not Satisfied' },
  { score: 1, label: 'No Comment' },
] as const

export type CsatScore = (typeof CSAT_RATINGS)[number]['score']

/** @deprecated Use CsatScore — kept for legacy imports */
export type CsatRating = CsatScore

const LEGACY_CSAT_LABELS: Record<string, string> = {
  not_satisfied: 'Not Satisfied',
  no_comment: 'No Comment',
  neutral: 'No Comment',
  dissatisfied: 'Not Satisfied',
  very_dissatisfied: 'Not Satisfied',
  satisfied: 'Satisfied',
  very_satisfied: 'Very Satisfied',
}

const LEGACY_CSAT_VALUES: Record<string, CsatScore> = {
  no_comment: 1,
  neutral: 1,
  not_satisfied: 2,
  dissatisfied: 2,
  very_dissatisfied: 2,
  satisfied: 3,
  very_satisfied: 4,
}

const SCORE_LABELS: Record<number, string> = Object.fromEntries(
  CSAT_RATINGS.map((r) => [r.score, r.label])
)

export function isValidCsatScore(value: number): value is CsatScore {
  return Number.isInteger(value) && value >= 1 && value <= 4
}

export function isCsatScore(value: number | string | null): value is CsatScore {
  if (value === null || value === '') return false
  const n = typeof value === 'number' ? value : Number(value)
  return isValidCsatScore(n)
}

/** @deprecated Use isCsatScore */
export const isCsatRating = isCsatScore

export function normalizeCsatScore(value: string | number | null): CsatScore | null {
  if (value === null || value === '') return null
  if (isCsatScore(value)) {
    return typeof value === 'number' ? value : (Number(value) as CsatScore)
  }
  const legacy = LEGACY_CSAT_VALUES[String(value).toLowerCase()]
  return legacy ?? null
}

/** @deprecated Use normalizeCsatScore */
export const normalizeCsatRating = normalizeCsatScore

export function getCsatScoreLabel(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'

  const score = normalizeCsatScore(value)
  if (score != null) {
    return SCORE_LABELS[score] ?? CSAT_RATINGS.find((r) => r.score === score)?.label ?? '—'
  }

  const legacyLabel = LEGACY_CSAT_LABELS[String(value).toLowerCase()]
  if (legacyLabel) return legacyLabel

  const decoded = sanitizeRestrictedLabelText(String(value))
  return decoded || '—'
}
